import React from "react"

import { CompanyRoleEnum } from "@boundless/bl-api-client/customer/models/CompanyRoleEnum"
import { CompanyRoleEnumLabels } from "@boundless/bl-api-client/customer/models/CompanyRoleEnumLabels"
import {
  getCustomerCompanyListQueryKey,
  getCustomerMembershipActivityListQueryKey,
  getCustomerMembershipListQueryKey,
  useCustomerMembershipDestroy,
  useCustomerMembershipPartialUpdate,
} from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { LoaderCircleIcon, UserMinusIcon } from "lucide-react"
import { toast } from "sonner"

import { CancelInvitationDialog } from "@/components/membership/CancelInvitationDialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { ErrorType } from "@/lib/clients"
import { formatDate } from "@/lib/utils"

import type {
  CompanyInvitation,
  Membership,
  MembershipMutationError,
  MembershipUser,
} from "@boundless/bl-api-client/customer/models"

type MemberStatus = "Active" | "Pending" | "Inactive"

/** "Pending" means the person has never signed in, not that an invite is open. */
function memberStatus(user: MembershipUser): MemberStatus {
  if (!user.lastLogin) {
    return "Pending"
  }
  return user.isActive ? "Active" : "Inactive"
}

const statusStyles: Record<MemberStatus, string> = {
  Active: "bg-green-100 text-green-800",
  Pending: "bg-yellow-100 text-yellow-800",
  Inactive: "bg-gray-100 text-gray-600",
}

function MembershipTableStructure({ children }: { children: React.ReactNode }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead className="hidden md:table-cell">Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead className="hidden lg:table-cell">Last Login</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-12">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  )
}

function mutationErrorMessage(
  error: ErrorType<MembershipMutationError>,
): string {
  return error.response?.data.detail ?? error.message
}

function memberName(user: MembershipUser): string {
  return `${user.firstName} ${user.lastName}`.trim() || user.email
}

interface Props {
  companyUuid: string
  memberships: Membership[]
  invitations?: CompanyInvitation[]
}

/**
 * Members of a company, followed by the invitations still awaiting acceptance.
 *
 * The two are separate row shapes rather than one union: an invitation has no
 * user behind it yet, so it has no name, no last login and no account state.
 */
export function MembershipTable({
  companyUuid,
  memberships,
  invitations = [],
}: Props) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const membershipQueryKey = getCustomerMembershipListQueryKey(companyUuid)
  // Prefix of the activity list key, so it also matches the infinite variant
  // the Activity page reads under.
  const activityQueryKey =
    getCustomerMembershipActivityListQueryKey(companyUuid)
  const [selfDemotionUserUuid, setSelfDemotionUserUuid] = React.useState<
    string | null
  >(null)
  const changeRole = useCustomerMembershipPartialUpdate<
    ErrorType<MembershipMutationError>
  >({
    mutation: {
      meta: { shouldDisplayErrorToast: false },
      onSuccess: async () => {
        toast.success("Company role updated")
        await queryClient.invalidateQueries({ queryKey: membershipQueryKey })
        await queryClient.invalidateQueries({ queryKey: activityQueryKey })
      },
      onError: (error) => {
        toast.error("Unable to change Company Role", {
          description: mutationErrorMessage(error),
        })
      },
    },
  })

  const removeMembership = useCustomerMembershipDestroy<
    ErrorType<MembershipMutationError>
  >({
    mutation: {
      meta: { shouldDisplayErrorToast: false },
      onSuccess: async (_, variables) => {
        const removedMembership = memberships.find(
          ({ user }) => user.uuid === variables.userUuid,
        )
        toast.success("Access removed")

        if (removedMembership?.isCurrentUser) {
          // Refresh access before the root route selects the next destination.
          await queryClient.invalidateQueries({
            queryKey: getCustomerCompanyListQueryKey(),
          })
          await navigate({ to: "/" })
          return
        }

        await queryClient.invalidateQueries({ queryKey: membershipQueryKey })
        await queryClient.invalidateQueries({ queryKey: activityQueryKey })
      },
      onError: (error) => {
        toast.error("Unable to remove access", {
          description: mutationErrorMessage(error),
        })
      },
    },
  })

  return (
    <MembershipTableStructure>
      {memberships.map((membership) => {
        const { canChangeRole, canRemove, role, user } = membership
        const status = memberStatus(user)
        const isChangingRole =
          changeRole.isPending && changeRole.variables.userUuid === user.uuid
        const isRemoving =
          removeMembership.isPending &&
          removeMembership.variables.userUuid === user.uuid

        return (
          <TableRow key={user.uuid} className="h-10">
            <TableCell>
              <div>{memberName(user)}</div>
              <div className="text-muted-foreground max-w-56 truncate text-xs md:hidden">
                {user.email}
              </div>
            </TableCell>
            <TableCell className="hidden md:table-cell">{user.email}</TableCell>
            <TableCell className="w-40">
              {canChangeRole ? (
                <Select
                  value={role}
                  disabled={isChangingRole}
                  onValueChange={(newRole) => {
                    if (
                      newRole !== CompanyRoleEnum.owner &&
                      newRole !== CompanyRoleEnum.collaborator
                    ) {
                      return
                    }
                    if (
                      membership.isCurrentUser &&
                      role === CompanyRoleEnum.owner &&
                      newRole === CompanyRoleEnum.collaborator
                    ) {
                      setSelfDemotionUserUuid(user.uuid)
                      return
                    }
                    changeRole.mutate({
                      companyUuid,
                      userUuid: user.uuid,
                      data: { role: newRole },
                    })
                  }}
                >
                  <SelectTrigger size="sm" className="w-36">
                    {isChangingRole ? (
                      <LoaderCircleIcon className="animate-spin" />
                    ) : (
                      <SelectValue />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CompanyRoleEnum.owner}>Owner</SelectItem>
                    <SelectItem value={CompanyRoleEnum.collaborator}>
                      Collaborator
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline">{CompanyRoleEnumLabels[role]}</Badge>
              )}
              <AlertDialog
                open={selfDemotionUserUuid === user.uuid}
                onOpenChange={(open) => {
                  if (!open) setSelfDemotionUserUuid(null)
                }}
              >
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Change your role to Collaborator?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      You will lose permission to manage company users and
                      roles. Another Owner will need to restore your Owner role.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className={buttonVariants({ variant: "destructive" })}
                      onClick={() => {
                        changeRole.mutate({
                          companyUuid,
                          userUuid: user.uuid,
                          data: { role: CompanyRoleEnum.collaborator },
                        })
                        setSelfDemotionUserUuid(null)
                      }}
                    >
                      Change my role
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </TableCell>
            <TableCell className="hidden w-32 lg:table-cell">
              {formatDate(user.lastLogin)}
            </TableCell>
            <TableCell className="w-28">
              <Badge variant="secondary" className={statusStyles[status]}>
                {status}
              </Badge>
            </TableCell>
            <TableCell className="w-12 text-right">
              {canRemove && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={removeMembership.isPending}
                      aria-label={`Remove access for ${memberName(user)}`}
                      title="Remove access"
                    >
                      {isRemoving ? (
                        <LoaderCircleIcon className="animate-spin" />
                      ) : (
                        <UserMinusIcon className="text-destructive" />
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove access?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will immediately revoke access to the company for{" "}
                        <span className="text-foreground font-medium">
                          {memberName(user)}
                        </span>
                        . They will receive an email about this change.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className={buttonVariants({ variant: "destructive" })}
                        disabled={removeMembership.isPending}
                        onClick={() =>
                          removeMembership.mutate({
                            companyUuid,
                            userUuid: user.uuid,
                          })
                        }
                      >
                        Remove access
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </TableCell>
          </TableRow>
        )
      })}
      {invitations.map((invitation) => (
        <TableRow key={invitation.uuid} className="text-muted-foreground h-10">
          <TableCell>{invitation.email}</TableCell>
          <TableCell className="hidden md:table-cell">
            {invitation.email}
          </TableCell>
          <TableCell className="w-40">
            <Badge variant="outline">
              {CompanyRoleEnumLabels[invitation.companyRole]}
            </Badge>
          </TableCell>
          <TableCell className="hidden w-32 lg:table-cell">&mdash;</TableCell>
          <TableCell className="w-28">
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              Invited
            </Badge>
          </TableCell>
          <TableCell className="w-12 text-right">
            <CancelInvitationDialog
              companyUuid={companyUuid}
              uuid={invitation.uuid}
              email={invitation.email}
            />
          </TableCell>
        </TableRow>
      ))}
    </MembershipTableStructure>
  )
}

export function FallbackMembershipTable() {
  return (
    <MembershipTableStructure>
      {Array.from({ length: 3 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell className="hidden md:table-cell">
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell className="w-32">
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell className="hidden w-32 lg:table-cell">
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell className="w-28">
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell className="w-12">
            <Skeleton className="size-8" />
          </TableCell>
        </TableRow>
      ))}
    </MembershipTableStructure>
  )
}
